import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import {
  Button,
  Input,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  getSharedReportSnapshot,
  getSharedReportStats,
  getSharedReportUrl,
  getSharedReports,
  setSharedReportActive,
  type SharedReport,
  type SharedReportStats,
} from 'common/src/api/sharedReports';
import Header from '../../components/layouts/Header';
import {
  ConfirmModal,
  EmptyState,
  LoadingState,
  PageLayout,
  StatusChip,
} from '../../components/ui';
import { useReportFormat } from '../../components/client-reports/useReportFormat';
import { useDownloadReportPdf } from '../../components/client-reports/downloadReportPdf';
import type { ClientReportSnapshot } from '../../utils/client-reports/types';

// Secciones de cada tipo de informe. Un reporte solo trae secciones de su
// tipo, así que se cuentan contra las de ese tipo ("3 de 3").
const SECTION_GROUPS: Array<Record<string, string>> = [
  {
    shipmentsToDate: 'Despachos acumulados a la fecha',
    upcomingArrivals: 'Arribos próximos 7 días',
    recentShipments: 'Últimos embarques despachados',
  },
  {
    pendingSettlementsSummary: 'Resumen de liquidaciones por recibir',
    settlementsByVessel: 'Liquidaciones por nave',
    overdueSettlements: 'Detalle por contenedor: críticas y vencidas',
    settlementsDueSoon: 'Detalle por contenedor: por vencer',
  },
];
const SECTION_LABELS: Record<string, string> = Object.assign({}, ...SECTION_GROUPS);
const sectionsInKind = (sections: string[]) => {
  const group = SECTION_GROUPS.find((labels) =>
    sections.some((section) => section in labels)
  );
  return group ? Object.keys(group).length : sections.length;
};

const ROWS_PER_PAGE = 20;
const NUMERIC_COLUMNS = new Set(['views', 'visitors']);

const ClientReportsPage = () => {
  const { t, dateTime, number } = useReportFormat();
  const [, setLocation] = useLocation();
  const { userProfile } = useUserStore();
  const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;

  const [reports, setReports] = useState<SharedReport[]>([]);
  const [stats, setStats] = useState<Map<number, SharedReportStats>>(new Map());
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [pdfReportId, setPdfReportId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Desactivar corta el acceso del cliente al instante: se confirma antes.
  const [toDeactivate, setToDeactivate] = useState<SharedReport | null>(null);
  const { download: downloadPdf } = useDownloadReportPdf();

  const handleDownloadPdf = async (report: SharedReport) => {
    setPdfReportId(report.id);
    try {
      const snapshot = await getSharedReportSnapshot<ClientReportSnapshot>(
        report.id
      );
      await downloadPdf([snapshot]);
    } catch (error) {
      console.error('Error loading report snapshot:', error);
      toast.error(t('No se pudo generar el PDF'));
    } finally {
      setPdfReportId(null);
    }
  };

  const load = useCallback(async () => {
    if (!exporterId) return;
    setLoading(true);
    try {
      const loaded = await getSharedReports(exporterId);
      const loadedStats = await getSharedReportStats(loaded.map((r) => r.id));
      setReports(loaded);
      setStats(new Map(loadedStats.map((s) => [s.report_id, s])));
    } catch (error) {
      console.error('Error loading shared reports:', error);
      toast.error(t('No se pudieron cargar los reportes'));
    } finally {
      setLoading(false);
    }
  }, [exporterId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const copyLink = async (token: string) => {
    await navigator.clipboard.writeText(getSharedReportUrl(token));
    toast.success(t('Link copiado'));
  };

  const toggleActive = async (report: SharedReport) => {
    setUpdatingId(report.id);
    try {
      await setSharedReportActive(report.id, !report.is_active);
      setReports((current) =>
        current.map((r) =>
          r.id === report.id
            ? {
                ...r,
                is_active: !report.is_active,
                deactivated_at: report.is_active
                  ? new Date().toISOString()
                  : null,
              }
            : r
        )
      );
      toast.success(
        report.is_active ? t('Link desactivado') : t('Link activado de nuevo')
      );
    } catch (error) {
      console.error('Error updating shared report:', error);
      toast.error(t('No se pudo actualizar el link'));
    } finally {
      setUpdatingId(null);
    }
  };

  const columns = useMemo(
    () => [
      { key: 'title', label: t('Reporte') },
      { key: 'sections', label: t('Secciones') },
      { key: 'created_at', label: t('Generado') },
      { key: 'views', label: t('Vistas') },
      { key: 'visitors', label: t('Personas') },
      { key: 'last_viewed_at', label: t('Última vista') },
      { key: 'status', label: t('Estado') },
      { key: 'actions', label: '' },
    ],
    [t]
  );

  // Busca por título (cliente, especie y temporada) y por los clientes.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return reports;
    return reports.filter((report) =>
      [report.title, ...report.receivers].some((text) =>
        text.toLowerCase().includes(query)
      )
    );
  }, [reports, search]);
  const pages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const visible = filtered.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  const renderCell = (report: SharedReport, key: string) => {
    const reportStats = stats.get(report.id);
    switch (key) {
      case 'title':
        return (
          <div className='flex flex-col'>
            <span className='whitespace-nowrap font-medium'>
              {report.title}
            </span>
            {report.receivers.length > 1 ? (
              <span className='text-xs text-default-500'>
                {report.receivers.join(' · ')}
              </span>
            ) : null}
          </div>
        );
      case 'sections':
        // Cantidad de secciones; el detalle va en el tooltip.
        return (
          <Tooltip
            content={report.sections
              .map((section) => t(SECTION_LABELS[section] || section))
              .join(' · ')}
          >
            <span className='whitespace-nowrap'>
              {t('{{total}} de {{all}}', {
                total: report.sections.length,
                all: sectionsInKind(report.sections),
              })}
            </span>
          </Tooltip>
        );
      case 'created_at':
        return dateTime(report.created_at);
      case 'views':
        return number(reportStats?.views ?? 0);
      case 'visitors':
        return number(reportStats?.visitors ?? 0);
      case 'last_viewed_at':
        return reportStats?.last_viewed_at
          ? dateTime(reportStats.last_viewed_at)
          : '—';
      case 'status':
        return (
          <StatusChip tone={report.is_active ? 'success' : 'default'}>
            {report.is_active ? t('Activo') : t('Desactivado')}
          </StatusChip>
        );
      case 'actions':
        return (
          <div className='flex justify-end gap-1'>
            <Tooltip content={t('Copiar link')}>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label={t('Copiar link')}
                isDisabled={!report.is_active}
                onPress={() => copyLink(report.share_token)}
              >
                <Icon icon='tabler:copy' width={18} />
              </Button>
            </Tooltip>
            <Tooltip content={t('Descargar PDF')}>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label={t('Descargar PDF')}
                isLoading={pdfReportId === report.id}
                onPress={() => handleDownloadPdf(report)}
              >
                <Icon icon='tabler:file-type-pdf' width={18} />
              </Button>
            </Tooltip>
            <Tooltip content={t('Abrir')}>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label={t('Abrir')}
                isDisabled={!report.is_active}
                onPress={() =>
                  window.open(getSharedReportUrl(report.share_token), '_blank')
                }
              >
                <Icon icon='tabler:external-link' width={18} />
              </Button>
            </Tooltip>
            <Tooltip
              content={
                report.is_active ? t('Desactivar link') : t('Activar link')
              }
            >
              <Button
                isIconOnly
                size='sm'
                variant='light'
                color={report.is_active ? 'danger' : 'default'}
                aria-label={
                  report.is_active ? t('Desactivar link') : t('Activar link')
                }
                isLoading={updatingId === report.id}
                onPress={() =>
                  report.is_active
                    ? setToDeactivate(report)
                    : toggleActive(report)
                }
              >
                <Icon
                  icon={report.is_active ? 'tabler:link-off' : 'tabler:link'}
                  width={18}
                />
              </Button>
            </Tooltip>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <Header
        title={t('Reportes')}
        description={t(
          'Reportes para tus clientes, compartidos como link. Cada link es una foto de los datos del día en que se generó.'
        )}
        actions={
          <Button
            color='primary'
            startContent={<Icon icon='tabler:plus' width={18} />}
            onPress={() => setLocation('/client-reports/new')}
          >
            {t('Nuevo reporte')}
          </Button>
        }
      />

      {loading ? (
        <LoadingState />
      ) : (
        <div className='flex flex-col gap-4'>
          {reports.length > 0 ? (
            <Input
              isClearable
              className='max-w-sm'
              placeholder={t('Buscar por cliente')}
              startContent={<Icon icon='tabler:search' />}
              value={search}
              onClear={() => setSearch('')}
              onValueChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          ) : null}
          <Table
            aria-label={t('Reportes')}
            bottomContent={
              pages > 1 ? (
                <div className='flex justify-center'>
                  <Pagination
                    aria-label={t('Páginas')}
                    page={page}
                    total={pages}
                    onChange={setPage}
                  />
                </div>
              ) : null
            }
          >
            <TableHeader columns={columns}>
              {(column) => (
                <TableColumn
                  key={column.key}
                  className={
                    NUMERIC_COLUMNS.has(column.key) ? 'text-right' : undefined
                  }
                >
                  {column.label}
                </TableColumn>
              )}
            </TableHeader>
            <TableBody
              items={visible}
              emptyContent={
                reports.length > 0 ? (
                  <EmptyState
                    icon='tabler:search'
                    title={t('No hay reportes para esa búsqueda')}
                  />
                ) : (
                  <EmptyState
                    icon='tabler:file-analytics'
                    title={t('Todavía no hay reportes')}
                    description={t('Crea el primero con "Nuevo reporte".')}
                  />
                )
              }
            >
              {(report) => (
                <TableRow key={report.id}>
                  {(columnKey) => (
                    <TableCell
                      className={
                        NUMERIC_COLUMNS.has(String(columnKey))
                          ? 'text-right'
                          : columnKey === 'created_at' ||
                              columnKey === 'last_viewed_at'
                            ? 'whitespace-nowrap'
                            : undefined
                      }
                    >
                      {renderCell(report, String(columnKey))}
                    </TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmModal
        isOpen={toDeactivate !== null}
        onClose={() => setToDeactivate(null)}
        onConfirm={async () => {
          if (toDeactivate) await toggleActive(toDeactivate);
          setToDeactivate(null);
        }}
        isDestructive
        isLoading={toDeactivate !== null && updatingId === toDeactivate.id}
        title={t('¿Desactivar el link?')}
        confirmLabel={t('Desactivar link')}
      >
        <p className='text-sm text-default-600'>
          {t(
            '{{title}} dejará de abrirse para quien tenga el link. Puedes volver a activarlo después.',
            {
              title: toDeactivate?.title ?? '',
            }
          )}
        </p>
      </ConfirmModal>
    </PageLayout>
  );
};

export default ClientReportsPage;
